import { useContext, useEffect, useState } from 'react'
import { CopilotUploadPreset } from 'shared/types/copilot'
import { CreationToolUploadContext } from '../../manager'
import { CreationToolUploadTag } from '../../types'
import { CopilotPresetSettings } from '../types'
import {
    channelIdFromPreset,
    isPublicFromPreset,
    ratingFromPreset,
    sourceUrlFromPreset,
    tagsFromPreset,
    usernameFromPreset,
} from '../utils/preset'

function didTagsChange(tags: CreationToolUploadTag[], sourceTags: CreationToolUploadTag[]) {
    let changed = false

    if (tags.length !== sourceTags.length) {
        return true
    }

    sourceTags.forEach((tag) => {
        const foundIndex = tags.findIndex(
            (t) => t.text === tag.text && (t.isFeatured || false) === (tag.isFeatured || false)
        )

        if (foundIndex < 0) {
            changed = true
        }
    })

    return changed
}

export function useHasChanges(activePreset: CopilotUploadPreset | null, settings: CopilotPresetSettings) {
    const context = useContext(CreationToolUploadContext)
    const [state, setState] = useState<boolean>(false)

    const { upload_collections, upload_is_hidden, upload_rating, upload_source_url, upload_tags, upload_channel } =
        activePreset || {}

    useEffect(() => {
        if (!activePreset) {
            setState(false)
            return
        }

        let changed = false

        if (settings.collections) {
            const channelId = channelIdFromPreset(activePreset)

            if (context.channelId !== channelId) {
                changed = true
            }
        }

        if (settings.rating) {
            const rating = ratingFromPreset(activePreset)

            if (context.rating !== rating) {
                changed = true
            }
        }

        if (settings.sourceUrl) {
            const sourceUrl = sourceUrlFromPreset(activePreset)

            if (context.sourceUrl !== sourceUrl) {
                changed = true
            }
        }

        if (settings.tags) {
            const tags = tagsFromPreset(activePreset)

            if (didTagsChange(tags, context.tags)) {
                changed = true
            }
        }

        if (settings.username) {
            const username = usernameFromPreset(activePreset)

            if (context.username !== username) {
                changed = true
            }
        }

        if (settings.visibility) {
            const isPublic = isPublicFromPreset(activePreset)

            if (context.isPublic !== isPublic) {
                changed = true
            }
        }

        setState(changed)
    }, [
        context,
        settings,
        upload_collections?.collections,
        upload_tags?.tags,
        upload_is_hidden,
        upload_rating,
        upload_source_url,
        upload_channel,
    ])

    return state
}
